<script setup lang="ts">
import { ref, computed } from 'vue';

import useContext from '@/composables/useContext';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import {
  generateHtmlElementId,
  getContext
} from '@/composables/useGiftProductListing';
import useGiftCard from '@/composables/useGiftCard';
import ProductCard from '../ProductCard.vue';

const props = defineProps({
  section: {
    type: Object,
    required: true
  },
  highlightSide: {
    type: String,
    default: ''
  },
});

const { page, route } = useContext();
const { websiteText } = useWebsiteTexts();
const { activeGiftCard } = useGiftCard();
const { isLoadingGiftProducts, loadAllSectionProducts, isSingleSection } =
  getContext();

const error = ref('');

const highlightProductClass = computed(() => {
  if (!props.highlightSide) {
    return '';
  }
  return `gift-listing-section__highlight-${props.highlightSide}`;
});

const productSection = ref(props.section);

const products = computed(() => {
  const productList = productSection.value?.products || [];

  if (productList?.[0] && props.highlightSide) {
    productList[0].highlighted = true;
  }

  return productList?.sort((a, b) => a.createdDate - b.createdDate);
});

const shouldRender = computed(() => {
  return products.value?.length > 0;
});

const categoryId = computed(() => {
  return route.query?.category || page.value.dataJson?.dataJson?.category?.systemId;
});

const tag = computed(() => {
  return productSection.value?.tags?.[0];
});

const sectionLinkId = computed(() => {
  return `${generateHtmlElementId(tag.value?.value || '')}`;
});

const sectionCaption = computed(() => {
  if (isSingleSection) {
    return tag.value.displayName || '';
  }

  return tag.value.displayName || tag.value.value || '';
});

const displayLoadMoreProducts = computed(() => {
  return (
    (productSection.value?.canLoadMoreProducts &&
      productSection.value?.take < productSection.value?.totalHits) ||
    false
  );
});

const productsLeftToDisplayCaption = computed(() => {
  const highlightTake = props.highlightSide
    ? productSection.value?.take - 3
    : productSection.value?.take;

  const left = productSection.value?.totalHits - highlightTake || 0;

  return websiteText(
    'getmygift__productlisting__productslefttodisplay'
  ).value?.replace('{0}', left);
});

async function triggerLoadAllProducts() {
  try {
    const activeSection = document.location.href.substring(
      document.location.href.indexOf('#')
    );
    if (activeSection) {
      history.replaceState(
        {},
        '',
        document.location.href.substring(0, document.location.href.indexOf('#'))
      );
    }

    await loadAllSectionProducts({
      categorySystemId: categoryId.value,
      section: {
        take: 999,
        tags: (productSection.value.tags || []).map((t: any) => {
          return {
            key: t.key,
            value: t.value
          };
        })
      }
    });
  } catch (err: any) {
    error.value = err;
  }
}

function pushSelectItemEvent(product) {
  if (typeof window.dataLayer === 'undefined' || !categoryId) {
    return;
  }
  const dataToPush = {
    event: productSection.value?.isPopularSection  ? 'select_item_recommendations' : 'select_item',
    item_list_id: `category_${categoryId.value}`,
    item_list_name: `Category: ${page.value.dataJson?.dataJson?.category?.googleAnalyticsName}`,
    items: [
      {
        item_id: product?.articleNumber || '',
        item_name:
          product?.googleAnalyticsName ||
          product?.productName ||
          product?.name ||
          '',
        item_brand: product?.brandName,
        quantity: 1,
        item_list_id: `category_${categoryId.value}`,
        item_list_name: `Category: ${page.value.dataJson?.dataJson?.category?.googleAnalyticsName}`
      }
    ]
  };

  window.dataLayer.push(dataToPush);
}

const showFavoriteProducts = computed(() => {
  return activeGiftCard?.value?.giftCards?.length > 0;
})

function isFavoriteProduct(productId) {
  return activeGiftCard?.value?.favoriteProductIds?.includes(productId);
}

</script>

<template>
  <div class="gift-listing-section" v-if="shouldRender" :id="sectionLinkId">
    <h3 class="gift-listing-section__caption">{{ sectionCaption }}</h3>
    <div
      class="gift-listing-section__product-list"
      :class="highlightProductClass"
    >
      <product-card
        v-for="(product, index) in products"
        :key="product"
        :product="product"
        :tag="tag"
        :category-id="categoryId"
        :isFavoriteProduct="isFavoriteProduct(product.articleNumber)"
        :showFavoriteProducts="showFavoriteProducts"
        @click="pushSelectItemEvent(product)"
      />
    </div>
    <div
      v-if="displayLoadMoreProducts"
      class="gift-listing-section__more-products"
    >
      <p class="gift-listing-section__caption-bottom category-subtitle">
        {{ sectionCaption }}
      </p>
      <p class="gift-listing-section__more-products-caption highlight">
        {{ productsLeftToDisplayCaption }}
      </p>
      <button
        class="btn-primary gift-listing-section__more-products-button"
        @click="triggerLoadAllProducts()"
        :disabled="isLoadingGiftProducts"
      >
        {{ websiteText('getmygift__productlisting__loadmoreproducts').value }}
      </button>
    </div>
  </div>
</template>

<style>
.gift-listing-section__caption {
  text-align: center;
  text-transform: uppercase;
  margin: 25px 0 20px;
}

.gift-listing-section__product-list {
  display: grid;
  gap: 4px;
  width: 100%;
  overflow: hidden;
  grid-template-rows: auto;
  margin-bottom: 20px;
}

.gift-listing-section__product-list .product-card {
  padding: 0;
}

.gift-listing-section__highlight-left .product-card:first-child {
  grid-row: span 2;
  grid-column: span 2;
}

.gift-listing-section__highlight-right .product-card:first-child {
  grid-row: span 2;
  grid-column: span 2;
  grid-column-end: -1;
  grid-row-end: 1;
}

.gift-listing-section__more-products {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: 10px;
}

.gift-listing-section__caption-bottom {
  margin: 0;
}

.gift-listing-section__more-products-button {
  text-transform: uppercase;
  width: auto;
}

.gift-listing-section__more-products-caption {
  text-align: center;
  margin: 6px 0 15px;
}

@media (--tabletAndDesktop) {
  .gift-listing-section__caption {
    margin: 20px 0 35px;
  }

  .gift-listing-section__more-products {
    margin: 10px 0 20px;
  }

  .gift-listing-section__more-products-button {
    padding: 15px 50px;
  }
}

@media (--phone) {
  .gift-listing-section__more-products-button {
    width: 100%;
  }

  .gift-listing-section__product-list {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}

@media (--tablet) {
  .gift-listing-section__product-list {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .gift-listing-section__highlight-left
    .product-card:first-child
    .product-card__image-spacer,
  .gift-listing-section__highlight-right
    .product-card:first-child
    .product-card__image-spacer {
    padding-top: calc(117.767% + 55px);
  }
}

@media (--desktop) {
  .gift-listing-section__product-list {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .gift-listing-section__highlight-left
    .product-card:first-child
    .product-card__image-spacer,
  .gift-listing-section__highlight-right
    .product-card:first-child
    .product-card__image-spacer {
    padding-top: calc(117.767% + 55px);
  }
}
</style>
